import { useState, useEffect } from 'react';
import { getAdminCampaigns, createCampaign, updateCampaign, deleteCampaign } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Calendar } from 'lucide-react';

// Convert UTC ISO from backend (e.g. "2026-04-30T08:00:00.000Z")
// → string for <input type="datetime-local"> in user's local time (Italy)
// e.g. "2026-04-30T10:00" during CEST.
const utcIsoToLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const emptyForm = {
  month_key: new Date().toISOString().slice(0, 7),
  title: '', subtitle: '', description: '', image_url: '',
  price: 0, compare_at_price: null, badge: '',
  featured_position: 1, is_active_manual: false, is_digital: false,
  active_from: '', active_until: '',
  promo_code: '', promo_discount_type: 'percentage', promo_discount_value: 0,
  main_site_url: '', sort_order: 1, hero_override: '',
  shopify_product_id: '', shopify_handle: '', inventory_status: 'in_stock',
  base_weekly_sales: 0, is_visible: true, long_description: '', features: '', video_url: ''
};

export default function AdminCampaigns() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await getAdminCampaigns();
      setCampaigns(res.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditingId(c.campaign_id);
    setForm({
      month_key: c.month_key || '', title: c.title || '', subtitle: c.subtitle || '',
      description: c.description || '', image_url: c.image_url || '',
      price: c.price || 0, compare_at_price: c.compare_at_price || null,
      badge: c.badge || '', featured_position: c.featured_position || 1,
      is_active_manual: c.is_active_manual || false,
      active_from: utcIsoToLocalInput(c.active_from),
      active_until: utcIsoToLocalInput(c.active_until),
      promo_code: c.promo_code || '', promo_discount_type: c.promo_discount_type || 'percentage',
      promo_discount_value: c.promo_discount_value || 0, main_site_url: c.main_site_url || '',
      sort_order: c.sort_order || 1, hero_override: c.hero_override || '',
      shopify_product_id: c.shopify_product_id || '', shopify_handle: c.shopify_handle || '',
      inventory_status: c.inventory_status || 'in_stock',
      base_weekly_sales: c.base_weekly_sales || 0,
      is_visible: c.is_visible !== false,
      long_description: c.long_description || '',
      features: c.features || '',
      video_url: c.video_url || ''
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = { ...form, price: parseFloat(form.price) || 0, promo_discount_value: parseFloat(form.promo_discount_value) || 0, sort_order: parseInt(form.sort_order) || 1, featured_position: parseInt(form.featured_position) || 1, base_weekly_sales: parseInt(form.base_weekly_sales) || 0 };
      if (form.compare_at_price) data.compare_at_price = parseFloat(form.compare_at_price);
      if (form.active_from) data.active_from = new Date(form.active_from).toISOString();
      if (form.active_until) data.active_until = new Date(form.active_until).toISOString();
      if (editingId) {
        await updateCampaign(editingId, data);
        toast.success('Campagna aggiornata');
      } else {
        await createCampaign(data);
        toast.success('Campagna creata');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa campagna?')) return;
    try {
      await deleteCampaign(id);
      toast.success('Campagna eliminata');
      load();
    } catch {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const setField = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const grouped = {};
  campaigns.forEach(c => {
    if (!grouped[c.month_key]) grouped[c.month_key] = [];
    grouped[c.month_key].push(c);
  });

  return (
    <div className="space-y-6" data-testid="admin-campaigns">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl tracking-tight" style={{ fontFamily: 'var(--font-heading)', fontWeight: 600 }}>Campagne</h1>
          <p className="font-body text-sm" style={{ color: 'var(--text-muted)' }}>Gestisci i prodotti del mese</p>
        </div>
        <Button onClick={openNew} data-testid="create-campaign-btn"
          className="rounded-sm font-body text-sm" style={{ backgroundColor: 'var(--accent)', color: '#fff' }}>
          <Plus className="w-4 h-4 mr-1" /> Nuova Campagna
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }} /></div>
      ) : Object.keys(grouped).length === 0 ? (
        <Card className="border rounded-sm" style={{ borderColor: 'var(--border-light)' }}>
          <CardContent className="py-12 text-center">
            <p className="font-body" style={{ color: 'var(--text-muted)' }}>Nessuna campagna. Crea la prima!</p>
          </CardContent>
        </Card>
      ) : (
        Object.entries(grouped).sort(([a], [b]) => b.localeCompare(a)).map(([month, camps]) => (
          <Card key={month} className="border rounded-sm" style={{ borderColor: 'var(--border-light)' }}>
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)', fontWeight: 600 }}>
                <Calendar className="w-4 h-4" style={{ color: 'var(--accent)' }} />
                {month}
                <Badge className="text-xs ml-2" variant="outline">{camps.length}/4 prodotti</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {camps.sort((a, b) => a.sort_order - b.sort_order).map(c => (
                  <div key={c.campaign_id} className="flex items-center gap-4 py-3 border-b last:border-b-0" style={{ borderColor: 'var(--border-light)' }}
                    data-testid={`campaign-row-${c.campaign_id}`}>
                    <img src={c.image_url} alt={c.title} className="w-12 h-16 object-cover rounded-sm flex-shrink-0" style={{ border: '1px solid var(--border-light)' }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-body text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{c.title}</p>
                        <Badge className="text-xs rounded-sm flex-shrink-0" style={{
                          backgroundColor: c.is_currently_active ? 'rgba(59,82,67,0.1)' : 'rgba(138,141,138,0.1)',
                          color: c.is_currently_active ? 'var(--accent-success)' : 'var(--text-muted)'
                        }}>
                          {c.is_currently_active ? 'ON' : 'OFF'}
                        </Badge>
                        <Badge className="text-[10px] rounded-sm flex-shrink-0 uppercase tracking-wider" style={{
                          backgroundColor: c.is_digital ? 'rgba(37,99,235,0.1)' : 'rgba(234,88,12,0.1)',
                          color: c.is_digital ? '#2563eb' : '#ea580c'
                        }} data-testid={`campaign-type-badge-${c.campaign_id}`}>
                          {c.is_digital ? 'Digitale' : 'Fisico'}
                        </Badge>
                      </div>
                      <p className="font-body text-xs" style={{ color: 'var(--text-muted)' }}>
                        Pos {c.featured_position} &middot; {c.price.toFixed(2)} EUR &middot; {c.promo_code || 'No promo'}
                      </p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`edit-campaign-${c.campaign_id}`}>
                        <Pencil className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(c.campaign_id)} data-testid={`delete-campaign-${c.campaign_id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))
      )}

      {/* Campaign Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="campaign-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl" style={{ fontFamily: 'var(--font-heading)' }}>
              {editingId ? 'Modifica Campagna' : 'Nuova Campagna'}
            </DialogTitle>
            <DialogDescription className="font-body text-sm" style={{ color: 'var(--text-secondary)' }}>
              {editingId ? 'Aggiorna i dettagli della campagna' : 'Configura un nuovo prodotto del mese'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Mese (YYYY-MM)</Label>
                <Input value={form.month_key} onChange={e => setField('month_key', e.target.value)} className="mt-1 rounded-sm text-sm" data-testid="form-month-key" />
              </div>
              <div>
                <Label className="text-xs">Posizione (1-4)</Label>
                <Input type="number" min={1} max={4} value={form.featured_position} onChange={e => setField('featured_position', e.target.value)} className="mt-1 rounded-sm text-sm" data-testid="form-position" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Titolo</Label>
              <Input value={form.title} onChange={e => setField('title', e.target.value)} className="mt-1 rounded-sm text-sm" data-testid="form-title" />
            </div>
            <div>
              <Label className="text-xs">Sottotitolo (autore)</Label>
              <Input value={form.subtitle} onChange={e => setField('subtitle', e.target.value)} className="mt-1 rounded-sm text-sm" data-testid="form-subtitle" />
            </div>
            <div>
              <Label className="text-xs">Descrizione</Label>
              <Input value={form.description} onChange={e => setField('description', e.target.value)} className="mt-1 rounded-sm text-sm" data-testid="form-description" />
            </div>
            <div>
              <Label className="text-xs">URL Immagine</Label>
              <Input value={form.image_url} onChange={e => setField('image_url', e.target.value)} className="mt-1 rounded-sm text-sm" data-testid="form-image-url" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Prezzo (EUR)</Label>
                <Input type="number" step="0.01" value={form.price} onChange={e => setField('price', e.target.value)} className="mt-1 rounded-sm text-sm" data-testid="form-price" />
              </div>
              <div>
                <Label className="text-xs">Prezzo Barrato</Label>
                <Input type="number" step="0.01" value={form.compare_at_price || ''} onChange={e => setField('compare_at_price', e.target.value)} className="mt-1 rounded-sm text-sm" data-testid="form-compare-price" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Badge</Label>
              <Input value={form.badge} onChange={e => setField('badge', e.target.value)} placeholder="es. Novita, Bestseller" className="mt-1 rounded-sm text-sm" data-testid="form-badge" />
            </div>
            <div>
              <Label className="text-xs">Tipo prodotto *</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button type="button" onClick={() => setField('is_digital', false)} data-testid="form-type-fisico"
                  className="p-3 rounded-sm border text-center text-sm font-medium transition-all"
                  style={{
                    borderColor: form.is_digital === false ? '#ea580c' : 'var(--border-light)',
                    borderWidth: form.is_digital === false ? 2 : 1,
                    backgroundColor: form.is_digital === false ? 'rgba(234,88,12,0.06)' : 'transparent',
                    color: form.is_digital === false ? '#ea580c' : 'var(--text-secondary)',
                  }}>
                  Fisico (libro, con spedizione)
                </button>
                <button type="button" onClick={() => setField('is_digital', true)} data-testid="form-type-digitale"
                  className="p-3 rounded-sm border text-center text-sm font-medium transition-all"
                  style={{
                    borderColor: form.is_digital === true ? '#2563eb' : 'var(--border-light)',
                    borderWidth: form.is_digital === true ? 2 : 1,
                    backgroundColor: form.is_digital === true ? 'rgba(37,99,235,0.06)' : 'transparent',
                    color: form.is_digital === true ? '#2563eb' : 'var(--text-secondary)',
                  }}>
                  Digitale (no spedizione)
                </button>
              </div>
              <p className="text-[11px] mt-1.5" style={{ color: 'var(--text-muted)' }}>
                Scegli "Fisico" per libri/dispense da spedire. "Digitale" solo per contenuti online (corsi ECM, ebook).
              </p>
            </div>
            <div className="flex items-center gap-3 py-2">
              <Switch checked={form.is_active_manual} onCheckedChange={v => setField('is_active_manual', v)} data-testid="form-active-switch" />
              <Label className="text-sm">Attivo manualmente (ON)</Label>
            </div>
            <div className="flex items-center gap-3 py-2">
              <Switch checked={form.is_visible} onCheckedChange={v => setField('is_visible', v)} data-testid="form-visible-switch" />
              <Label className="text-sm">Visibile in homepage e landing page</Label>
            </div>
            <div>
              <Label className="text-xs">Descrizione estesa (landing page)</Label>
              <textarea value={form.long_description} onChange={e => setField('long_description', e.target.value)}
                placeholder="Descrizione completa per la pagina prodotto..." rows={4}
                className="mt-1 w-full rounded-sm border text-sm px-3 py-2 resize-y" style={{ borderColor: 'var(--border-light)' }}
                data-testid="form-long-description" />
            </div>
            <div>
              <Label className="text-xs">Caratteristiche (una per riga)</Label>
              <textarea value={form.features} onChange={e => setField('features', e.target.value)}
                placeholder={"Oltre 1100 pagine\n6557 immagini cliniche\n23 video dimostrativi"} rows={3}
                className="mt-1 w-full rounded-sm border text-sm px-3 py-2 resize-y" style={{ borderColor: 'var(--border-light)' }}
                data-testid="form-features" />
            </div>
            <div>
              <Label className="text-xs">URL Video (YouTube, Vimeo o link diretto)</Label>
              <Input value={form.video_url} onChange={e => setField('video_url', e.target.value)}
                placeholder="es. https://www.youtube.com/watch?v=... oppure https://vimeo.com/..."
                className="mt-1 rounded-sm text-sm" data-testid="form-video-url" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Inizio disponibilita <span style={{ color: 'var(--text-muted)' }}>(ora italiana)</span></Label>
                <Input type="datetime-local" value={form.active_from} onChange={e => setField('active_from', e.target.value)} className="mt-1 rounded-sm text-sm" data-testid="form-active-from" />
              </div>
              <div>
                <Label className="text-xs">Fine disponibilita <span style={{ color: 'var(--text-muted)' }}>(ora italiana)</span></Label>
                <Input type="datetime-local" value={form.active_until} onChange={e => setField('active_until', e.target.value)} className="mt-1 rounded-sm text-sm" data-testid="form-active-until" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Codice Promo</Label>
                <Input value={form.promo_code} onChange={e => setField('promo_code', e.target.value)} className="mt-1 rounded-sm text-sm font-mono" data-testid="form-promo-code" />
              </div>
              <div>
                <Label className="text-xs">Tipo Sconto</Label>
                <select value={form.promo_discount_type} onChange={e => setField('promo_discount_type', e.target.value)}
                  className="mt-1 w-full h-9 rounded-sm border text-sm px-2" style={{ borderColor: 'var(--border-light)' }} data-testid="form-discount-type">
                  <option value="percentage">%</option>
                  <option value="fixed">EUR</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Valore Sconto</Label>
                <Input type="number" step="0.01" value={form.promo_discount_value} onChange={e => setField('promo_discount_value', e.target.value)} className="mt-1 rounded-sm text-sm" data-testid="form-discount-value" />
              </div>
            </div>
            <div>
              <Label className="text-xs">URL Sito Principale</Label>
              <Input value={form.main_site_url} onChange={e => setField('main_site_url', e.target.value)} className="mt-1 rounded-sm text-sm" data-testid="form-main-url" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Ordine</Label>
                <Input type="number" min={1} value={form.sort_order} onChange={e => setField('sort_order', e.target.value)} className="mt-1 rounded-sm text-sm" data-testid="form-sort-order" />
              </div>
              <div>
                <Label className="text-xs">Vendite base (social proof)</Label>
                <Input type="number" min={0} value={form.base_weekly_sales} onChange={e => setField('base_weekly_sales', e.target.value)} className="mt-1 rounded-sm text-sm" data-testid="form-base-sales" />
              </div>
            </div>
            <Button onClick={handleSave} disabled={saving} data-testid="save-campaign-btn"
              className="w-full rounded-sm font-body" style={{ backgroundColor: 'var(--accent)', color: '#fff' }}>
              {saving ? 'Salvataggio...' : editingId ? 'Aggiorna' : 'Crea Campagna'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
